// S9 · Terminology settings (`/settings/terminology`, Module 02 §4 S9, §2.5).
//
// The tenant's own vocabulary: the 61 canonical term keys × the six locales,
// each showing the EFFECTIVE label and the layer that produced it (tenant →
// pack → base, D-032). Rename one, or reset one back.
//
// Lives OUTSIDE the wizard shell on purpose (§4 S9: "Available post-go-live") —
// terminology is a permanent setting owned by module 02, not a wizard step, so
// nothing here freezes when the tenant goes live.
//
// API (all through the generated api-client, doctrine 3):
//   GET    /v1/terminology?locale=<each locale>    → six reads, merged into rows
//   PUT    /v1/terminology/{term_key}           → {locale, label}
//   DELETE /v1/terminology/{term_key}?locale=   → reset
//
// Permissions (§7): READ is open to any authenticated principal (the floor PWA
// consumes the same feed), WRITE needs `onboarding.edit_terminology` —
// group_admin only. The client mirror is fail-closed and only decides whether
// the edit controls are offered; the server is the authorisation boundary.

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../components/breadcrumbs";
import { DataTable, type DataTableColumn } from "../../../components/data-table";
import { EmptyState, ErrorState, TableSkeleton } from "../../../components/states";
import { UnauthorizedError } from "../../../lib/problem";
import { requestIdOf } from "../../../lib/request";
import {
  useResetTerminology,
  useTerminology,
  useUpsertTerminology,
} from "../hooks/onboarding-queries";
import { useOnboardingPermissions } from "../hooks/permissions";
import { termFallbackKey } from "../hooks/terminology";
import { TermCell, type CellStatus } from "./terminology/term-cell";
import {
  TERM_LOCALES,
  buildTermRows,
  filterTermRows,
  termStats,
  type SourceFilter,
  type TermLocaleCode,
} from "./terminology/terminology-model";
import "./terminology/terminology.css";

const SOURCE_FILTERS: readonly SourceFilter[] = ["all", "overridden", "tenant", "pack", "base"];

/** Identifies the cell a mutation is about, so its pending/error/status lands on
 * that cell instead of flashing across the whole grid. */
function cellId(termKey: string, locale: TermLocaleCode): string {
  return `${termKey}:${locale}`;
}

/** Terms on one page of the grid (design-system §3.17 rev-7). 61 terms in
 * four locales made a page 4,241 px tall at 1440 x 900; 20 keep it under the
 * declutter gate's four screens. */
export const TERM_PAGE_SIZE = 20;

export function TerminologySettingsScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const perms = useOnboardingPermissions();

  // One read per locale — the endpoint resolves a single locale per call (§5).
  // Fixed hook order (TERM_LOCALES is a frozen literal) and a 5-minute
  // staleTime in the shared hook, so this is six requests on first paint.
  const en = useTerminology("en");
  const ru = useTerminology("ru");
  const zh = useTerminology("zh");
  const fr = useTerminology("fr");
  const zhHant = useTerminology("zh-Hant");
  const hi = useTerminology("hi");
  const byLocale: Record<TermLocaleCode, typeof en> = {
    en,
    ru,
    zh,
    fr,
    "zh-Hant": zhHant,
    hi,
  };

  const upsert = useUpsertTerminology();
  const reset = useResetTerminology();

  const [q, setQ] = useState("");
  const [source, setSource] = useState<SourceFilter>("all");
  const [active, setActive] = useState<string | null>(null);
  const [status, setStatus] = useState<{ id: string; kind: CellStatus } | null>(null);
  const [failure, setFailure] = useState<{ id: string; kind: "save" | "reset" } | null>(null);

  const unauthorized = TERM_LOCALES.some((l) => byLocale[l].error instanceof UnauthorizedError);
  useEffect(() => {
    if (unauthorized) void navigate({ to: "/login" });
  }, [unauthorized, navigate]);

  const rows = useMemo(
    () =>
      buildTermRows({
        en: en.data?.items,
        ru: ru.data?.items,
        zh: zh.data?.items,
        fr: fr.data?.items,
      }),
    [en.data, ru.data, zh.data, fr.data],
  );
  const visible = useMemo(() => filterTermRows(rows, { q, source }), [rows, q, source]);
  const stats = termStats(rows);

  // §7: writing needs onboarding.edit_terminology (group_admin). Fail-closed
  // while the role feed settles, so no control flashes that the server refuses.
  const canEdit = perms.has("onboarding.edit_terminology");

  const anyPending = TERM_LOCALES.some((l) => byLocale[l].isPending);
  const firstError: unknown =
    TERM_LOCALES.map((l) => byLocale[l].error).find((e) => e !== null) ?? undefined;

  function refetchAll(): void {
    for (const locale of TERM_LOCALES) void byLocale[locale].refetch();
  }

  function save(termKey: string, locale: TermLocaleCode, label: string): void {
    const id = cellId(termKey, locale);
    setActive(id);
    setFailure(null);
    setStatus(null);
    upsert.mutate(
      { termKey, body: { locale, label } },
      {
        onSuccess: () => {
          setStatus({ id, kind: "saved" });
          setActive(null);
        },
        onError: () => {
          setFailure({ id, kind: "save" });
          setActive(null);
        },
      },
    );
  }

  function resetTerm(termKey: string, locale: TermLocaleCode): void {
    const id = cellId(termKey, locale);
    setActive(id);
    setFailure(null);
    setStatus(null);
    reset.mutate(
      { termKey, locale },
      {
        onSuccess: (entry) => {
          // The response says which layer answers NOW. Per D-035 a tenant-edited
          // pack term lands on `base`, so the screen echoes what the server
          // actually reported instead of promising the pack label came back.
          setStatus({ id, kind: entry.source === "pack" ? "reset_pack" : "reset_base" });
          setActive(null);
        },
        onError: () => {
          setFailure({ id, kind: "reset" });
          setActive(null);
        },
      },
    );
  }

  const header = (
    <div>
      <PageHeader title={t("onboarding.terminology.title")} />
      <p className="tm-subtitle">{t("onboarding.terminology.subtitle")}</p>
      <p className="tm-note">{t("onboarding.terminology.layerNote")}</p>
    </div>
  );

  if (anyPending || unauthorized) {
    return (
      <div className="tm-screen">
        {header}
        <TableSkeleton rows={8} cols={5} label={t("onboarding.terminology.loading")} />
      </div>
    );
  }

  if (firstError !== undefined) {
    return (
      <div className="tm-screen">
        {header}
        <ErrorState
          title={t("onboarding.terminology.errorTitle")}
          detail={t("onboarding.terminology.errorDetail")}
          retryLabel={t("onboarding.state.retry")}
          onRetry={refetchAll}
          requestId={requestIdOf(firstError)}
          requestIdLabel={t("onboarding.state.requestId")}
        />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="tm-screen">
        {header}
        <EmptyState
          title={t("onboarding.terminology.noTermsTitle")}
          body={t("onboarding.terminology.noTermsBody")}
          ctaLabel={t("onboarding.state.retry")}
          onCta={refetchAll}
        />
      </div>
    );
  }

  return (
    <div className="tm-screen">
      {header}
      {perms.isResolved && !canEdit ? (
        <p className="tm-note">{t("onboarding.terminology.readOnlyNote")}</p>
      ) : (
        <p className="tm-note">{t("onboarding.terminology.resetNote")}</p>
      )}

      <div className="tm-toolbar">
        <div className="tm-field">
          <label htmlFor="tm-search">{t("onboarding.terminology.searchLabel")}</label>
          <input
            id="tm-search"
            className="ds-input"
            type="search"
            placeholder={t("onboarding.terminology.searchPlaceholder")}
            value={q}
            onChange={(event) => {
              setQ(event.target.value);
            }}
          />
        </div>
        <div className="tm-field">
          <label htmlFor="tm-source">{t("onboarding.terminology.sourceLabel")}</label>
          <select
            id="tm-source"
            className="ds-select"
            value={source}
            onChange={(event) => {
              setSource(event.target.value as SourceFilter);
            }}
          >
            {SOURCE_FILTERS.map((value) => (
              <option key={value} value={value}>
                {t(`onboarding.terminology.source.${value}`)}
              </option>
            ))}
          </select>
        </div>
        <p className="tm-stats">
          {t("onboarding.terminology.stats", {
            terms: stats.terms,
            overridden: stats.overriddenTerms,
          })}
        </p>
      </div>

      {visible.length === 0 ? (
        <EmptyState
          title={t("onboarding.terminology.emptyTitle")}
          body={t("onboarding.terminology.emptyBody")}
          ctaLabel={t("onboarding.terminology.clear")}
          onCta={() => {
            setQ("");
            setSource("all");
          }}
        />
      ) : (
        // UX-6b: the 61×4 grid on `DataTable` (D-219 — editable cells are
        // `render` props returning the same TermCell as before, not a reason
        // for a raw table). `.tm-table` survives as an UNSTYLED hook on the
        // wrapper: the certified responsive net locates the grid by it and
        // asserts its `thead` drops at 412 px + every locale cell carries
        // `data-label` — both now the primitive's own collapse contract.
        <div className="tm-table-wrap tm-table">
          <DataTable
            columns={[
              {
                key: "term",
                header: t("onboarding.terminology.colTerm"),
                render: (row) => {
                  const termName = t(termFallbackKey(row.termKey), { defaultValue: row.termKey });
                  // The key is for support, not for the reader (rev-7 audit
                  // #32): it stays on hover and in `data-term`, not on glass.
                  return (
                    <span className="tm-term-name" title={row.termKey}>
                      {termName}
                    </span>
                  );
                },
              },
              ...TERM_LOCALES.map((locale): DataTableColumn<(typeof visible)[number]> => ({
                key: locale,
                header: t(`onboarding.terminology.colLocale.${locale}`),
                render: (row) => {
                  const id = cellId(row.termKey, locale);
                  const termName = t(termFallbackKey(row.termKey), {
                    defaultValue: row.termKey,
                  });
                  return (
                    <TermCell
                      termKey={row.termKey}
                      termName={termName}
                      locale={locale}
                      cell={row.cells[locale]}
                      isLoading={byLocale[locale].isPending}
                      canEdit={canEdit}
                      busy={active === id}
                      status={status?.id === id ? status.kind : null}
                      failed={failure?.id === id ? failure.kind : null}
                      onSave={(label) => {
                        save(row.termKey, locale, label);
                      }}
                      onReset={() => {
                        resetTerm(row.termKey, locale);
                      }}
                    />
                  );
                },
              })),
            ]}
            rows={visible}
            rowKey={(row) => row.termKey}
            empty={null}
            rowProps={(row) => ({ "data-term": row.termKey })}
            clientPager={{
              pageSize: TERM_PAGE_SIZE,
              resetKey: `${q}\u0000${source}`,
              range: (from, to, total) => {
                const n = new Intl.NumberFormat(i18n.language);
                return t("onboarding.terminology.pageRange", {
                  from: n.format(from),
                  to: n.format(to),
                  total: n.format(total),
                });
              },
              prevLabel: t("onboarding.terminology.prevPage"),
              nextLabel: t("onboarding.terminology.nextPage"),
            }}
          />
        </div>
      )}
    </div>
  );
}
